import { Sparkles } from 'lucide-react'
import type { ReactNode } from 'react'
import { Button } from '@/components/ui/Button'
import { formatDuration } from '@/lib/format'
import styles from './ScriptDock.module.scss'

export interface ScriptSegment {
  text: string
  duration_hint?: number | null
}

export interface ScriptDockProps {
  hook: string
  segments: ScriptSegment[]
  closing: string
  /** Target duration for the AI suggestion. Optional; stub when absent. */
  targetDurationHint?: string
}

interface Block {
  section: string
  start: number
  end: number
  text: string
  keyword?: string
}

/**
 * Roteiro dock content — shows the script broken into hook / body segments
 * / closing with cumulative timestamps derived from each segment's
 * `duration_hint`. Last block is the AI suggestion card (static stub; the
 * real suggestion engine wires in with the Claude call during Delivery 4).
 */
export function ScriptDock({ hook, segments, closing, targetDurationHint }: ScriptDockProps) {
  const blocks = composeBlocks(hook, segments, closing)
  const totalWords = countWords(hook, segments, closing)

  return (
    <div className={styles.dock}>
      <h4 className={styles.title}>
        Roteiro · {blocks.length} blocos · {totalWords} palavras
      </h4>

      {blocks.map((b, idx) => (
        <article key={idx} className={styles.block}>
          <div className={styles.ts}>
            {formatDuration(b.start)} — {formatDuration(b.end)} · {b.section}
          </div>
          <div className={styles.text}>{highlightKeyword(b.text, b.keyword)}</div>
        </article>
      ))}

      <aside className={styles.ai}>
        <h4 className={styles.aiTitle}>
          <Sparkles aria-hidden /> Sugestão do Prism
        </h4>
        <p className={styles.aiBody}>
          {targetDurationHint
            ? `Duração alvo ${targetDurationHint}. Ajustes automáticos disponíveis.`
            : 'Sugestões automáticas para hook, ritmo e CTA final. Em breve.'}
        </p>
        <div className={styles.aiActions}>
          <Button size="sm" disabled>
            Regenerar hook
          </Button>
          <Button size="sm" variant="ghost" disabled>
            Comparar 3 versões
          </Button>
        </div>
      </aside>
    </div>
  )
}

function composeBlocks(hook: string, segments: ScriptSegment[], closing: string): Block[] {
  const blocks: Block[] = []
  let cursor = 0

  // Hook — rough 4s if we don't have a hint (reads ~10 words in that window).
  const hookDur = estimateDuration(hook, 4)
  blocks.push({ section: 'hook', start: cursor, end: cursor + hookDur, text: hook })
  cursor += hookDur

  segments.forEach((seg, i) => {
    const dur = seg.duration_hint ?? estimateDuration(seg.text, 6)
    blocks.push({
      section: segments.length === 1 ? 'corpo' : `corpo ${(i + 1).toString()}`,
      start: cursor,
      end: cursor + dur,
      text: seg.text,
    })
    cursor += dur
  })

  const closingDur = estimateDuration(closing, 5)
  blocks.push({ section: 'outro', start: cursor, end: cursor + closingDur, text: closing })

  return blocks
}

function countWords(hook: string, segments: ScriptSegment[], closing: string): number {
  const all = [hook, ...segments.map((s) => s.text), closing].join(' ')
  return all.trim().split(/\s+/).filter(Boolean).length
}

/** Rough reading time when the backend didn't include a per-segment hint. */
function estimateDuration(text: string, fallback: number): number {
  const words = text.trim().split(/\s+/).filter(Boolean).length
  if (words === 0) return fallback
  // ~160 words per minute for narration → ~2.67 wps.
  return Math.max(2, Math.round(words / 2.67))
}

function highlightKeyword(text: string, keyword?: string): ReactNode {
  if (!keyword) return text
  const idx = text.toLowerCase().indexOf(keyword.toLowerCase())
  if (idx === -1) return text
  return (
    <>
      {text.slice(0, idx)}
      <mark className={styles.mark}>{text.slice(idx, idx + keyword.length)}</mark>
      {text.slice(idx + keyword.length)}
    </>
  )
}
